import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Box, Divider, Page, Tabs, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  EnterpriseListingEvent,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent,
} from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyBoostAdsWebApiHandler_getBoostListingDetails,
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserPropertyFeedOverrideWebApiHandler_getFeedOverrideSections,
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get,
  useRentalManagerUserSelfServiceOnboardingV2WebApiHandler_submitManualListing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { calculateRequiredFieldsErrors } from '../../components/MFAccordion/utils';
import { AccordionStateProvider } from '../../contexts/AccordionStateContext';
import {
  getListingDetailsQueryKey,
  useGetListingDetails,
  useGetListingRecommendations,
  useGetTours,
} from '../../hooks/queries/properties';
import { useActivateListing } from '../../hooks/queries/useActivateListing';
import { clearExpiredReimportLock, useReimportLock } from '../../hooks/useReimportLock';
import { useSyndicationUpdates } from '../../hooks/useSyndicationUpdates';
import accessUtils from '../../utils/accessUtils';
import { getPortfolioOrigin } from '../../utils/navigationCacheUtils';

import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';
import { LISTING_TABS_KEYS, LISTING_TABS_OPTIONS } from './components/MFSubHeader/components/ListingNavTabs/constants';

import { useAuth } from '../../components/AuthProvider';
import InsightCards from '../../components/CompetitiveInsights/InsightCards';
import ListingActivationTerms from '../../components/ListingActivationTerms';
import ListingCompletenessScore from '../../components/ListingCompletenessScore';
import { AccordionStateButton } from '../../components/ListingDetails/AccordionStateButton';
import Banners from '../../components/ListingDetails/Banners';
import DuplicateListingDetails from '../../components/ListingDetails/DuplicateListingDetails';
import Footer from '../../components/ListingDetails/Footer';
import ListingHeader from '../../components/ListingDetails/ListingHeader';
import ListingStats from '../../components/ListingDetails/ListingStats';
import { MFAccordion } from '../../components/MFAccordion';
import ActivateListingErrorModal from '../../components/modals/ActivateListingErrorModal';
import PreActivationModal from '../../components/modals/PreActivationModal';
import TransferFeedListingModal from '../../components/modals/TransferFeedListingModal';
import ReimportSyncingBanner from '../../components/ReimportSyncingBanner';
import StickyContainer from '../../components/StickyContainer';

import { EVENT_IDS } from '../../analytics/events/reviewPage';
import MetricsSummaryPage from '../MetricsSummaryPage';
import AccordionStateHeader from './AccordionStateHeader';
import { getListingBreadcrumb } from './breadcrumb';
import PropertyCard from './PropertyCard';

import type { FreemiumDatarouterError } from '../../components/modals/FreemiumErrorModal';

const PropertyDetailsContainer = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();
  const { user } = useAuth();

  const [activateListingError, setActivateListingError] = useState<FreemiumDatarouterError | null>(null);
  const [displayPreActivationModal, setDisplayPreActivationModal] = useState(false);

  // The Expand/Collapse-all control lives above the accordion (in ListingHeader, or a standalone
  // fallback when there's no ListingHeader). Observe it so the sticky header can take over its copy
  // of the button once it scrolls out of view — never showing both at once.
  const listingHeaderRef = useRef<HTMLDivElement | null>(null);
  const [isListingHeaderVisible, setIsListingHeaderVisible] = useState(true);

  useEffect(() => {
    const el = listingHeaderRef.current;
    if (!el) return;
    // Hand off when the control scrolls behind everything pinned at the top: the sticky header
    // (~152px) plus, under the IA nav, the fixed top bar above it (--ia-topnav-total-height, 0 otherwise).
    const iaTopBarHeight = parseInt(getComputedStyle(el).getPropertyValue('--ia-topnav-total-height'), 10) || 0;
    const observer = new window.IntersectionObserver(([entry]) => setIsListingHeaderVisible(entry.isIntersecting), {
      threshold: 0.1,
      rootMargin: `-${152 + iaTopBarHeight}px 0px 0px 0px`,
    });
    observer.observe(el);
    return () => observer.disconnect();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- ListingHeader only renders once listingDetails loads, so the ref is null at mount; re-running on ref.current is what attaches the observer afterwards
  }, [listingHeaderRef.current]);

  const { listingAlias } = router.query as { listingAlias: string };

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const hasCompetitiveInsights =
    accessUtils.canAccessCompetitiveInsights() &&
    Boolean(listingDetails?.isMultifamily && listingDetails?.adproduct?.customerZrnNumber);

  useSyndicationUpdates({ listingAlias });

  const { isReimportLocked } = useReimportLock({ listingAlias });

  // Refetch the queries whose values or section state the feed re-import may have reverted, so the UI stops
  // showing stale cached data once the lock clears.
  const invalidateReimportAffectedQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getListingDetailsQueryKey(listingAlias) });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyFeedOverrideWebApiHandler_getFeedOverrideSections.getKey({
        listingAlias,
      }),
    });
    // The reimport can change recommendations (e.g. required-field errors driving MFAccordion), which are keyed
    // by a meta tag rather than a query key, so invalidate them the same way property edits do.
    queryClient.invalidateQueries({
      predicate: (query) =>
        Boolean((query.meta?.tags as ReadonlyArray<string> | undefined)?.includes('listingRecommendations')),
    });
  }, [listingAlias, queryClient]);

  // In-place clear: the lock's TTL elapses (or another tab clears it) while the page is open. Track the alias
  // alongside the last state so navigating to a different listing isn't mistaken for a lock -> unlock transition.
  const lastLockStateRef = useRef({ listingAlias, wasLocked: false });
  useEffect(() => {
    const prev = lastLockStateRef.current;
    if (prev.listingAlias === listingAlias && prev.wasLocked && !isReimportLocked) {
      invalidateReimportAffectedQueries();
    }
    lastLockStateRef.current = { listingAlias, wasLocked: isReimportLocked };
  }, [isReimportLocked, listingAlias, invalidateReimportAffectedQueries]);

  // Return-after-expiry: the lock was active when the user left and its TTL elapsed while away, so no timer ran
  // to clear it. The container mounts already unlocked, so clear the stale entry and invalidate on mount instead.
  useEffect(() => {
    if (clearExpiredReimportLock(listingAlias)) {
      invalidateReimportAffectedQueries();
    }
  }, [listingAlias, invalidateReimportAffectedQueries]);

  const { recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: listingDetails?.isMultifamily,
  });

  const { data: costsAndFeesData } = useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get(
    { listingAlias },
    { enabled: Boolean(listingAlias && listingDetails?.isMultifamily), meta: { tags: ['mf-costsAndFees'] } },
  );

  const { data: boostListingData } = useRentalManagerUserPropertyBoostAdsWebApiHandler_getBoostListingDetails(
    { customerId: listingDetails?.customerId ?? '', listingAlias },
    {
      enabled: Boolean(listingDetails?.customerId && listingAlias),
      meta: {
        tags: ['boostListingDetails', listingAlias],
        context: {
          api: 'getBoostListingDetails',
          details: { customerId: listingDetails?.customerId ?? '', listingAlias, userEmail: user?.email },
        },
      },
    },
  );

  const { vrModels, externalTours } = useGetTours(listingAlias, listingDetails);

  const requiredFieldsErrors = useMemo(
    () =>
      calculateRequiredFieldsErrors(
        listingDetails
          ? {
              ...listingDetails,
              vrModels: vrModels.map((vrModel) => ({ ...vrModel, creatorZuid: String(vrModel.creatorZuid) })),
              externalTours,
            }
          : undefined,
        recommendationsBySection,
        costsAndFeesData,
        boostListingData,
      ),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    [listingDetails, recommendationsBySection, costsAndFeesData, vrModels, externalTours],
  );

  const { mutate: submitListing } = useRentalManagerUserSelfServiceOnboardingV2WebApiHandler_submitManualListing({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'Submitting manual self-serve onboarding listing for review',
      },
    },
  });

  const { mutate: activateListing, isPending: isActivateListingPending } = useActivateListing();

  // Determine selected tab from URL pathname
  const selectedListingNav = router.pathname.includes('/metrics')
    ? LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.METRICS].id
    : LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.LISTING].id;

  useEffect(() => {
    if (user && listingDetails && user.customers.length && !user.impersonatorEmail && !user.impersonatorUserToken) {
      RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent({
        feedId: listingDetails.feedId,
        feedListingId: listingDetails.feedListingId,
        event: EnterpriseListingEvent.VIEW_PROPERTY,
      }).catch(() => {});
    }
  }, [listingAlias, listingDetails, user]);

  const activateAndSyncListing = () => {
    activateListing(
      { postData: { listingAlias } },
      {
        onSuccess: () => {
          window.scrollTo(0, 0);
          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({ listingAlias }),
          });
        },
        onError: (error) => {
          setActivateListingError(error as FreemiumDatarouterError);

          logError({
            error,
            errorType: 'HpppActivationError',
            context: 'PropertyDetailsContainer.activateAndSyncListing',
          });
        },
      },
    );
  };

  const handlePublishClick = () => {
    if (listingDetails?.isFeed || listingDetails?.singleFamilyContractDto?.isRequired) {
      setDisplayPreActivationModal(true);

      return;
    }

    if (
      listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress &&
      !listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted
    ) {
      submitListing(
        { postData: { listingAlias } },
        {
          onSuccess: () => {
            window.location.href = `${EXTERNAL_ROUTES.SETUP_TASKS}?alias=${listingAlias}`;
          },
          onError: (err) => {
            enqueueToast(<Toast tone="critical">{DatarouterError.getDisplayMessage(err)}</Toast>);
          },
        },
      );
      return;
    }

    // activate listing, then call handleChargeAndActivateListingResponse onSuccess
    activateAndSyncListing();
  };

  const renderPageContent = () => {
    const canPublish = Object.keys(requiredFieldsErrors).every(
      (key) => !requiredFieldsErrors[key as keyof typeof requiredFieldsErrors]?.isIncomplete,
    );

    if (selectedListingNav === LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.LISTING].id) {
      return (
        <>
          <Banners />
          {showDupeDetails && !canFeedConnectSwitchSource && <DuplicateListingDetails />}
          {user?.isEnterpriseCustomer && !hasCompetitiveInsights && <ListingStats />}
          {!hasCompetitiveInsights && (
            <Box css={{ marginBottom: '600' }}>
              <ListingCompletenessScore
                requiredFieldsErrors={Object.values(requiredFieldsErrors)
                  .filter((value) => value?.isIncomplete)
                  .map((value) => value.isIncomplete)}
                isMultifamily={listingDetails?.isMultifamily ?? false}
                isFeed={listingDetails?.isFeed ?? false}
              />
            </Box>
          )}
          {showFeedListingHeader && (
            <ListingHeader ref={listingHeaderRef} title={`Source: ${listingDetails?.feedName}`} />
          )}
          {showSwitchSourceListingHeader && <ListingHeader ref={listingHeaderRef} />}
          {!hasListingHeader && (
            <Box
              ref={listingHeaderRef}
              css={{
                display: 'flex',
                flexDirection: { base: 'column', md: 'row' },
                alignItems: { base: 'flex-start', md: 'center' },
                justifyContent: 'flex-end',
                paddingBottom: '600',
              }}
            >
              <AccordionStateButton isVisible={true} />
            </Box>
          )}

          <ReimportSyncingBanner isReimportInProgress={isReimportLocked} />

          <MFAccordion requiredFieldsErrors={requiredFieldsErrors} />

          <ListingActivationTerms
            isTextCenter={true}
            actionText={`By ${listingDetails?.active ? 'deactivating' : 'publishing'} this listing`}
          />
          <Footer
            onPublishClick={handlePublishClick}
            isPublishedDisabled={!canPublish}
            isPublishLoading={isActivateListingPending}
          />
        </>
      );
    }

    if (selectedListingNav === LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.METRICS].id) {
      return <MetricsSummaryPage />;
    }
  };

  const showDupeDetails = !!listingDetails?.topDupesDetails?.dupes?.length;
  const canFeedConnectSwitchSource = user?.features?.canSwitchListingSource ?? false;

  const showFeedListingHeader = Boolean(listingDetails?.isFeed);
  const showSwitchSourceListingHeader = !listingDetails?.isFeed && canFeedConnectSwitchSource;
  const hasListingHeader = showFeedListingHeader || showSwitchSourceListingHeader;

  const breadcrumb = getListingBreadcrumb({
    listingAlias,
    isSelfServeOnboardingInProgress: listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress,
    origin: getPortfolioOrigin() ?? undefined,
  });

  return (
    <Page.Root
      data-testid="PropertyDetailsContainer-Page"
      css={{ backgroundColor: 'bg.neutral' }}
      data-eventid={listingDetails?.active ? EVENT_IDS.ACTIVE_LISTING_VIEW : undefined}
    >
      <Page.Breadcrumb css={{ paddingInline: '0', marginInline: '0' }}>
        <Box css={{ paddingInline: '400' }}>
          <TextButton asChild icon={<IconChevronLeftFilled />}>
            <a href={breadcrumb.url}>{breadcrumb.title}</a>
          </TextButton>
        </Box>
      </Page.Breadcrumb>

      <Page.Content
        css={{
          backgroundColor: 'bg.neutral',
          padding: '0 !important',
          minHeight: '65vh',
          marginBottom: '128px',
        }}
      >
        <Box
          css={{
            padding: 'default',
            paddingBottom: '0',
            md: {
              border: '1px solid {colors.border.subtle}',
              borderRadius: '100',
              // Keep this border off the left nav's divider; flush, the two 1px lines read as one dark border.
              marginInline: 'default',
            },
          }}
        >
          <PropertyCard />
          {hasCompetitiveInsights && <InsightCards />}

          {!hasCompetitiveInsights && (
            <Box css={{ paddingBottom: '600', marginTop: '400' }}>
              {listingDetails?.lastActivatedMs ? (
                <Tabs.Root selected={selectedListingNav}>
                  <Tabs.List aria-label="Listing sections">
                    {[LISTING_TABS_KEYS.LISTING, LISTING_TABS_KEYS.METRICS].map((key) => {
                      const tabOption = LISTING_TABS_OPTIONS[key];
                      const href =
                        tabOption.id === 'metrics'
                          ? { pathname: ROUTES.METRICS, query: { listingAlias } }
                          : { pathname: ROUTES.LISTING, query: { listingAlias } };

                      return (
                        <Tabs.Tab key={tabOption.id} value={tabOption.id} asChild>
                          <Link href={href}>{tabOption.label}</Link>
                        </Tabs.Tab>
                      );
                    })}
                  </Tabs.List>
                </Tabs.Root>
              ) : (
                <Divider />
              )}
            </Box>
          )}

          <AccordionStateProvider>
            {selectedListingNav === LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.LISTING].id && (
              <StickyContainer passDownFixedStatus={true} addBorderBottom={true}>
                <AccordionStateHeader isListingHeaderVisible={isListingHeaderVisible} />
              </StickyContainer>
            )}
            {renderPageContent()}
          </AccordionStateProvider>
        </Box>
      </Page.Content>

      <PreActivationModal
        displayPreActivationModal={displayPreActivationModal}
        setDisplayPreActivationModal={setDisplayPreActivationModal}
        onSubmit={activateAndSyncListing}
      />

      <TransferFeedListingModal listingAlias={listingAlias} isMultifamily={listingDetails?.isMultifamily} />

      {activateListingError && (
        <ActivateListingErrorModal onClose={() => setActivateListingError(null)} error={activateListingError} />
      )}
    </Page.Root>
  );
};

export default PropertyDetailsContainer;
